import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import {
  AddDocumentIcon,
  MoveDocumentIcon,
  PencilIcon,
} from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useCustomerStorageStore } from "@store/customerStorage/customerStorage";
import { type IUseModalProps } from "@store/modal/modal";
import { genericCustomerStorageRequest } from "@services/customerStorage/services";
import type {
  ICustomerStorageItemAction,
  IFolderData,
} from "@services/customerStorage/types";

interface IFolderModalForm {
  name?: string;
  moveToFolderId: number | null;
}

type TFolderModalVariant = "edit" | "create" | "move";

interface IFolderModalProps extends IUseModalProps {
  action: ICustomerStorageItemAction;
  variant: TFolderModalVariant;
  name?: string;
  folderDataAction?: ICustomerStorageItemAction;
}

export const FolderModal: React.FC<IFolderModalProps> = ({
  action,
  variant,
  folderDataAction,
  name = "",
  ...rest
}) => {
  const { t } = useTranslation();
  const modal = useModal(rest);
  const { currentFolderUrl } = useCustomerStorageStore();
  const queryClient = useQueryClient();

  const { data: availableFolders } = useQuery({
    queryKey: ["customerStorage", "folderData"],
    queryFn: () =>
      genericCustomerStorageRequest<{ folders: IFolderData[] }>({
        requestProps: folderDataAction,
      }),
    enabled: !!folderDataAction,
  });

  const { mutateAsync: mutateFolder, isPending } = useMutation({
    mutationFn: (data: IFolderModalForm) =>
      genericCustomerStorageRequest({
        requestProps: action,
        data,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["customerStorage", currentFolderUrl],
      });
      void queryClient.invalidateQueries({
        queryKey: ["customerStorage", "search"],
      });

      toast.success(
        t(`customerStorage.actions.folder.${variant}.toast.success`),
      );
      modal.close();
    },
  });

  const { handleSubmit, control, setError } = useForm<IFolderModalForm>({
    defaultValues: {
      name,
      moveToFolderId: 0,
    },
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitFolder: SubmitHandler<IFolderModalForm> = async (data) => {
    await mutateFolder(data).catch(setApiErrors);
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon:
          variant === "create"
            ? AddDocumentIcon
            : variant === "edit"
              ? PencilIcon
              : MoveDocumentIcon,
        color: "green",
      }}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={modal.close}
            type="button"
            className="w-full"
          >
            {t(`customerStorage.actions.folder.${variant}.buttons.cancel`)}
          </Button>
          <Button
            type="submit"
            className="w-full"
            isLoading={isPending}
            onClick={handleSubmit(submitFolder)}
          >
            {t(`customerStorage.actions.folder.${variant}.buttons.submit`)}
          </Button>
        </>
      }
      title={t(`customerStorage.actions.folder.${variant}.title`)}
      className="md:max-w-xl"
    >
      <form onSubmit={handleSubmit(submitFolder)}>
        {variant === "move" && availableFolders?.folders.length ? (
          <FormSelect
            name="moveToFolderId"
            options={availableFolders.folders.map((folder) => {
              return {
                value: folder.folderId,
                label: folder.shouldTranslate
                  ? t(`customerStorage.actions.folder.${folder.name}`)
                  : folder.name,
              };
            })}
            rules={{ required: true }}
            control={control}
            label={t("customerStorage.form.fields.folder.moveToFolderId.label")}
          />
        ) : (
          <FormInput
            name="name"
            rules={{ required: true }}
            control={control}
            label={t("customerStorage.form.fields.folder.name.label")}
          />
        )}
      </form>
    </Modal>
  );
};
